// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaymentMethod, type PaymentMethodResponse } from '../models/payment-method';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { type CardPayment, type PaymentResponse, FuturePaymentRequest } from '../models/payment';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from './util.service';
import { PaymentAccountType } from '../enums/paymentaccounttype.enum';
import { type AutoPayCancel } from '../models/autopay-cancel';
import { type AccountDetail } from '../models/account-detail';
import { type FuturePayment } from '../models/future-payment';
import { addDays } from 'date-fns';
import { type APIError } from '../models/apierror';

interface ApiResponse {
  Error?: APIError;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  constructor(
    private readonly http: HttpClient,
    private readonly utilService: UtilService,
  ) {}

  addNewPayMethod(paymethodList: PaymentMethod[], newMethod: PaymentMethod): void {
    if (newMethod.IsDefault) {
      paymethodList.forEach((method) => {
        method.IsDefault = false;
      });
    }
    paymethodList.push(newMethod);
  }

  removeCurrentOneTimePayMethod(paymethodList: PaymentMethod[]): void {
    paymethodList.forEach((method) => {
      if (!this.utilService.isNullOrWhitespace(method.ReferenceId)) {
        const removeMethodIndex = paymethodList.indexOf(method);
        paymethodList.splice(removeMethodIndex, 1);
      }
    });
  }

  getPreferredPayments(accountDetail?: AccountDetail): Observable<PaymentMethodResponse> {
    if (!accountDetail || !this.validateAccountNumber(accountDetail.AccountNumber)) {
      return throwError(() => new Error('Invalid account number'));
    }

    const url = `${environment.OASUrl()}/payment/preferred-payments`;
    const params = new HttpParams().set('accountNumber', accountDetail.AccountNumber);
    const res = this.http.get<PaymentMethodResponse>(url, { params }).pipe(
      catchError((dataError: any) => {
        const errorMessage = dataError?.error?.Message ?? 'GetPreferredPayments error';
        return throwError(() => new Error(errorMessage));
      }),
      map((data: PaymentMethodResponse) => {
        if (data.Error === null) {
          data.PaymentMethods?.forEach((pm) => {
            pm.BankCardDigital = this.isCardBankDigital(pm.PaymentAccountType);
            if (pm.BankCardDigital !== 'DIGITAL') {
              pm.Description = this.getPaymethodDescription(pm.PaymentAccountType, pm.LastFour);
            } else {
              pm.Description = pm.PaymentAccountType?.toString();
            }
            pm.IsAssociatedAutoPay = this.isPaymethodOnAutoPay(accountDetail, pm);
          });
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
    return res;
  }

  getPaymethodDescription(
    paytype: PaymentAccountType | undefined,
    ending4: string | undefined,
    nickname: string | null = null,
  ): string {
    if (!paytype) {
      paytype = PaymentAccountType.Unknown;
    }
    if (!ending4) {
      ending4 = 'XXXX';
    }
    if (nickname) {
      return `${nickname} ending in ${ending4}`;
    }
    switch (paytype) {
      case PaymentAccountType.Banking:
        return `Bank ending in ${ending4}`;
      default:
        return `${paytype} ending in ${ending4}`;
    }
  }

  isPaymethodOnAutoPay(accountDetail?: AccountDetail, pm?: PaymentMethod): boolean {
    if (accountDetail?.AutoPay !== null) {
      const type = this.isCardBankDigital(accountDetail?.AutoPay?.PaymentAccountType);
      switch (type) {
        case 'DIGITAL':
          return accountDetail?.AutoPay?.PaymentAccountType === pm?.PaymentAccountType;
        default:
          return accountDetail?.AutoPay?.AccountNumber === pm?.LastFour;
      }
    }
    return false;
  }

  updatePreferredPayment(prefPaymentData: PaymentMethod): Observable<ApiResponse> {
    const url = `${environment.OASUrl()}/payment/change-preferred-payment`;
    const res = this.http.post<ApiResponse>(url, prefPaymentData);
    return res.pipe(
      map((data: ApiResponse) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  makeCreditCardPayment(paymentData: CardPayment): Observable<PaymentResponse> {
    const url = `${environment.OASUrl()}/payment/make-card-payment`;
    const res = this.http.post<PaymentResponse>(url, paymentData);

    return res.pipe(
      map((data: PaymentResponse) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  makeBankPayment(paymentData: CardPayment): Observable<PaymentResponse> {
    const url = `${environment.OASUrl()}/payment/make-bank-payment`;
    const res = this.http.post<PaymentResponse>(url, paymentData);

    return res.pipe(
      map((data: PaymentResponse) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  postFuturePayment(paymentData: FuturePaymentRequest): Observable<FuturePayment> {
    const url = `${environment.OASUrl()}/payment/post-future-payment`;
    const res = this.http.post<FuturePayment>(url, paymentData);

    return res.pipe(
      map((data: FuturePayment) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  cancelAutoPayBank(autoPayCancelData: AutoPayCancel): Observable<ApiResponse> {
    const url = `${environment.OASUrl()}/autopay/cancel-bank-autopay`;
    const res = this.http.post<ApiResponse>(url, autoPayCancelData);

    return res.pipe(
      map((data: ApiResponse) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  cancelAutoPayCard(autoPayCancelData: AutoPayCancel): Observable<ApiResponse> {
    const url = `${environment.OASUrl()}/autopay/cancel-card-autopay`;
    const res = this.http.post<ApiResponse>(url, autoPayCancelData);

    return res.pipe(
      map((data: ApiResponse) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  enrollAutoPay(autoPayData: any): Observable<ApiResponse> {
    const url = `${environment.OASUrl()}/autopay/enroll-autopay`;
    const res = this.http.post<ApiResponse>(url, autoPayData);

    return res.pipe(
      map((data: ApiResponse) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  GetFuturePayment(accountNumber: string): Observable<ApiResponse> {
    if (!this.validateAccountNumber(accountNumber)) {
      return throwError(() => new Error('Invalid account number'));
    }

    const url = `${environment.OASUrl()}/payment/future-payment-history`;
    const params = new HttpParams().set('accountNumber', accountNumber);
    const res = this.http.get<ApiResponse>(url, { params });
    return res.pipe(
      map((data: ApiResponse) => {
        return data;
      }),
    );
  }

  validateRoutingNumber(req: any): Observable<ApiResponse> {
    const url = `${environment.OASUrl()}/payment/validate-routing-number`;
    const res = this.http.post<ApiResponse>(url, req);
    return res;
  }

  /**
   * Payment Util Functions
   */
  getNewPayMethodsList(
    newPaymethodsList: PaymentMethod[],
    bankLabel: string,
    cardLabel: string,
    savedPayMethods: any,
    disableDigitalPayments: boolean,
  ): void {
    this.addNewCardAndBankToPaymethodList(newPaymethodsList, bankLabel, cardLabel);

    if (!disableDigitalPayments) {
      // get list of unused digital payments
      const digitalPayTypes = [
        PaymentAccountType.ApplePay,
        PaymentAccountType.GooglePay,
        PaymentAccountType.PayPal,
        PaymentAccountType.Venmo,
      ].filter((pat) => {
        // return digital pay types that aren't in use
        if (savedPayMethods !== null) {
          return (
            savedPayMethods.findIndex((digitalPayment: any) => {
              return digitalPayment?.PaymentAccountType === pat;
            }) < 0
          );
        } else {
          return pat;
        }
      });

      digitalPayTypes.forEach((dp) => {
        const newDP = new PaymentMethod();
        newDP.Description = `New ${dp.toString()}`;
        newDP.PaymentAccountType = dp;
        newPaymethodsList.push(newDP);
      });
    }
  }

  addNewCardAndBankToPaymethodList(paymethodsList: PaymentMethod[], bankLabel: string, cardLabel: string): void {
    const newBankPayment = new PaymentMethod();
    newBankPayment.ReferenceId = '';
    newBankPayment.Description = bankLabel;
    newBankPayment.PaymentAccountType = PaymentAccountType.Banking;
    paymethodsList?.push(newBankPayment);

    const newCardPayment = new PaymentMethod();
    newCardPayment.ReferenceId = '';
    newCardPayment.Description = cardLabel;
    newCardPayment.PaymentAccountType = PaymentAccountType.Card;
    paymethodsList?.push(newCardPayment);
  }

  /**
   *
   * @param paymethodType Takes PaymentAccountType and gives you general classification
   * @returns string 'BANK', 'CARD', 'DIGITAL', 'CASH', 'UNKNOWN'
   */
  isCardBankDigital(paymethodType: PaymentAccountType | undefined): string {
    switch (paymethodType) {
      case PaymentAccountType.Banking || PaymentAccountType.Banking.toString():
        return 'BANK';
      case PaymentAccountType.Visa || PaymentAccountType.Visa.toString():
      case PaymentAccountType.MasterCard || PaymentAccountType.MasterCard.toString():
      case PaymentAccountType.AmericanExpress || PaymentAccountType.AmericanExpress.toString():
      case PaymentAccountType.Discover || PaymentAccountType.Discover.toString():
      case PaymentAccountType.Card || PaymentAccountType.Card.toString():
        return 'CARD';
      case PaymentAccountType.ApplePay || PaymentAccountType.ApplePay.toString():
      case PaymentAccountType.GooglePay || PaymentAccountType.GooglePay.toString():
      case PaymentAccountType.PayPal || PaymentAccountType.PayPal.toString():
      case PaymentAccountType.Venmo || PaymentAccountType.Venmo.toString():
        return 'DIGITAL';
      case PaymentAccountType.Cash || PaymentAccountType.Cash.toString():
        return 'CASH';
      default:
        return 'UNKNOWN';
    }
  }

  sortPaymentMethods(
    paymethodsList: PaymentMethod[],
    disableDigitalPayments: boolean,
    isLimitedPaymentOptions: boolean = false,
    addUnusedMethods = true,
    apFirst = false,
  ): PaymentMethod[] {
    let sortedList: PaymentMethod[] = [];
    // Clone the list to avoid mutations using proper shallow clone
    let newList: PaymentMethod[] = this.clonePaymentMethods(paymethodsList);
    const expiredCards = newList.filter((pm: PaymentMethod) => {
      if (pm.ExpDate) {
        if (this.isCardExpired(pm.ExpDate)) {
          pm.IsExpired = true;
          return true;
        }
        return false;
      } else {
        return false;
      }
    });

    newList = newList.filter((pm: PaymentMethod) => {
      return !expiredCards.includes(pm);
    });

    if (apFirst) {
      const apPayMethod: PaymentMethod | undefined = newList.find((pm: PaymentMethod) => {
        return pm.IsAssociatedAutoPay;
      });

      if (apPayMethod) {
        sortedList.push(apPayMethod);
        newList = newList.filter((pm: PaymentMethod) => {
          return !pm.IsAssociatedAutoPay;
        });
      }
    }

    const defaultPayMethod: PaymentMethod | undefined = newList.find((pm: PaymentMethod) => {
      return pm.IsDefault;
    });

    if (defaultPayMethod) {
      sortedList.push(defaultPayMethod);
      newList = newList.filter((pm: PaymentMethod) => {
        return !pm.IsDefault;
      });
    }

    // nicknamed payments
    const nicknamedPayMethods: PaymentMethod[] = newList.filter((pm: PaymentMethod) => {
      return pm.Nickname;
    });

    sortedList = sortedList.concat(
      nicknamedPayMethods.sort((a, b) => {
        const nameA = (a.Nickname ?? '').toUpperCase();
        const nameB = (b.Nickname ?? '').toUpperCase();

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        // names must be equal
        return 0;
      }),
    );

    newList = newList.filter((pm: PaymentMethod) => {
      return !nicknamedPayMethods.includes(pm);
    });

    // credit cards on ascending last 4
    const cardsWithLast4 = newList.filter((pm: PaymentMethod) => {
      return pm.BankCardDigital === 'CARD' && pm.LastFour;
    });

    sortedList = sortedList.concat(
      cardsWithLast4.sort((a, b) => {
        const nameA = a.LastFour ?? '';
        const nameB = b.LastFour ?? '';

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        // names must be equal
        return 0;
      }),
    );

    newList = newList.filter((pm: PaymentMethod) => {
      return !cardsWithLast4.includes(pm);
    });

    // expired cards
    sortedList = sortedList.concat(expiredCards);

    // bank ascending on last 4
    const bankWithLast4 = newList.filter((pm: PaymentMethod) => {
      return pm.BankCardDigital === 'BANK' && pm.LastFour;
    });

    sortedList = sortedList.concat(
      bankWithLast4.sort((a, b) => {
        const nameA = a.LastFour ?? '';
        const nameB = b.LastFour ?? '';

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        // names must be equal
        return 0;
      }),
    );
    newList = newList.filter((pm: PaymentMethod) => {
      return !bankWithLast4.includes(pm);
    });

    // digital payments
    const digitalPayMethods = newList.filter((pm: PaymentMethod) => {
      return (
        pm.BankCardDigital === 'DIGITAL' &&
        !this.utilService.isNullOrUndefined(pm.ReferenceId) &&
        !disableDigitalPayments
      );
    });

    sortedList = sortedList.concat(
      digitalPayMethods.sort((a, b) => {
        const nameA = a.PaymentAccountType ?? PaymentAccountType.Unknown;
        const nameB = b.PaymentAccountType ?? PaymentAccountType.Unknown;

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        // names must be equal
        return 0;
      }),
    );
    newList = newList.filter((pm: PaymentMethod) => {
      return !digitalPayMethods.includes(pm);
    });
    if (addUnusedMethods) {
      sortedList = this.addNewPayMethods(
        newList,
        sortedList,
        defaultPayMethod,
        digitalPayMethods,
        disableDigitalPayments,
      );
    }

    // digital payments not in use(alphabetical)
    // if LPO set dont show bank, venmo, amazon pay
    const isLPO = isLimitedPaymentOptions;
    if (isLPO) {
      sortedList = sortedList.filter((pm: PaymentMethod) => {
        return (
          pm.PaymentAccountType !== PaymentAccountType.Banking && pm.PaymentAccountType !== PaymentAccountType.Venmo
        );
      });
    }

    return sortedList;
  }

  addNewPayMethods(
    newList: any,
    sortedList: any,
    defaultPayMethod: any,
    digitalPayMethods: any,
    disableDigitalPayments: boolean,
  ): any {
    // ------------------------------------------------------------------------------------- //
    // new card
    const newCardIndex: number = newList.findIndex((pm: PaymentMethod) => {
      return !pm.ReferenceId && pm.PaymentAccountType === PaymentAccountType.Card;
    });
    if (newCardIndex > -1) {
      sortedList.push(newList[newCardIndex]);
      newList.splice(newCardIndex, 1);
    }

    // new bank
    const newBankIndex: number = newList.findIndex((pm: PaymentMethod) => {
      return !pm.ReferenceId && pm.PaymentAccountType === PaymentAccountType.Banking;
    });
    if (newBankIndex > -1) {
      sortedList.push(newList[newBankIndex]);
      newList.splice(newBankIndex, 1);
    }

    // get list of unused digital payments
    const digitalPayTypes = [
      PaymentAccountType.ApplePay,
      PaymentAccountType.GooglePay,
      PaymentAccountType.PayPal,
      PaymentAccountType.Venmo,
    ].filter((pat) => {
      // return digital pay types that aren't in use
      return (
        digitalPayMethods.findIndex((digitalPayment: any) => {
          return digitalPayment.PaymentAccountType === pat;
        }) < 0
      );
    });

    // create list of paymethods for unused digital pay types
    let unusedDigitalPayments: PaymentMethod[] = [];
    if (!disableDigitalPayments) {
      digitalPayTypes.forEach((dp) => {
        const newDP = new PaymentMethod();
        newDP.Description = `New ${dp.toString()}`;
        newDP.PaymentAccountType = dp;
        unusedDigitalPayments.push(newDP);
      });
    }
    // remove digital paymethod if it's default pay method
    unusedDigitalPayments = unusedDigitalPayments.filter((dp) => {
      return dp.PaymentAccountType !== defaultPayMethod?.PaymentAccountType;
    });

    sortedList = sortedList.concat(
      unusedDigitalPayments.sort((a, b) => {
        const nameA = a.PaymentAccountType ?? PaymentAccountType.Unknown;
        const nameB = b.PaymentAccountType ?? PaymentAccountType.Unknown;

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        // names must be equal
        return 0;
      }),
    );

    const cashPayment = newList.find((dp: any) => {
      return dp.PaymentAccountType === PaymentAccountType.Cash;
    });
    if (cashPayment) {
      sortedList.push(cashPayment);
    }
    return sortedList;
  }

  isCardExpired(expDate: string | undefined): boolean {
    if (!expDate) {
      return false;
    }

    const month = parseInt(expDate.slice(0, 2), 10);
    const year = parseInt(expDate.slice(2), 10);

    const currentDate = new Date();
    const currMonth = currentDate.getMonth() + 1;
    const currYear = currentDate.getFullYear();

    if (year < currYear || (year === currYear && month < currMonth)) {
      return true;
    }
    return false;
  }

  buildCancelFutureDatePaymentRequest(accountDetail: any): FuturePaymentRequest {
    const futurePayment = new FuturePaymentRequest();
    futurePayment.Person = accountDetail.Person;
    futurePayment.AccountNumber = accountDetail.FuturePayment?.Info?.AccountNumber;
    futurePayment.ActionCode = 'C';
    futurePayment.CustomerClass = accountDetail.CustomerClass;
    futurePayment.Date = accountDetail.FuturePayment?.Info?.PaymentDate;
    futurePayment.Amount = accountDetail.FuturePayment?.Info?.PaymentAmount;
    futurePayment.ReferenceNumber = accountDetail.FuturePayment?.Info?.ReferenceNumber;
    futurePayment.PaymentAccountType = accountDetail.FuturePayment?.PaymentMethod?.PaymentAccountType;
    futurePayment.BankAccountType = accountDetail.FuturePayment?.PaymentMethod?.BankAccountType;
    return futurePayment;
  }

  isEligibleForFuturePayment(accountDetail: any): boolean {
    // 1. If the account has a future dated payment, not eligible (early exit)
    if (accountDetail.FuturePayment != null) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    const todayPlus1 = addDays(today, 1);
    const todayPlus30 = addDays(today, 30);

    // 2. If on Pay Plan
    if (accountDetail.IsPayPlan && accountDetail.PaymentPlans) {
      // Check if pay plan type is ACTIVEACCTS or INACTIVE
      if (accountDetail.PayPlanType === 'ACTIVEACCTS' || accountDetail.PayPlanType === 'INACTIVE') {
        // Check if there's an installment date > today and < 30 days from today
        const hasEligibleInstallmentDate = accountDetail.PaymentPlans.some((plan: { DueDate: any }) => {
          const dueDate = new Date(plan.DueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate.getTime() > today.getTime() && dueDate.getTime() < todayPlus30.getTime();
        });
        return hasEligibleInstallmentDate; // Eligible if has valid installment, otherwise not eligible
      } else {
        // Pay plan type is not ACTIVEACCTS or INACTIVE - not eligible
        return false;
      }
    }

    // 3. Not on pay plan - check disqualifying conditions
    if (
      accountDetail.IsInCollections === true ||
      accountDetail.IsSevered === true ||
      accountDetail.IsAutoPay === true ||
      accountDetail.IsLimitedPaymentOptions === true
    ) {
      return false;
    }

    // 4. Check eligibility: balance > 0 AND due date > (today + 1 day) AND due date < (today + 30 days)
    if (accountDetail.CurrentBalance <= 0) {
      return false;
    }

    if (!accountDetail.CurrentBalanceDueDate) {
      return false;
    }

    const dueDate = new Date(accountDetail.CurrentBalanceDueDate);
    dueDate.setHours(0, 0, 0, 0);

    // Due date must be greater than 1 day from current date and less than 30 days from current date
    if (dueDate.getTime() > todayPlus1.getTime() && dueDate.getTime() < todayPlus30.getTime()) {
      return true;
    }

    return false;
  }

  /**
   * Validation Helper Methods
   */

  /**
   * Validates account number format and existence
   * @param accountNumber Account number to validate
   * @returns true if valid, false otherwise
   */
  private validateAccountNumber(accountNumber: string | undefined): boolean {
    if (!accountNumber || typeof accountNumber !== 'string') {
      return false;
    }

    // Remove whitespace
    const trimmed = accountNumber.trim();

    // Check if empty after trimming
    if (trimmed.length === 0) {
      return false;
    }

    // Check for minimum and maximum length (adjust based on your business rules)
    if (trimmed.length < 5 || trimmed.length > 20) {
      return false;
    }

    // Check if it contains only alphanumeric characters (adjust based on your business rules)
    if (!/^[a-zA-Z0-9-]+$/.test(trimmed)) {
      return false;
    }

    return true;
  }

  /**
   * Validates payment data including amount and account number
   * @param paymentData Payment data to validate
   * @returns Error message if invalid, null if valid
   */
  private validatePaymentData(paymentData: any): string | null {
    // Validate account number
    if (!this.validateAccountNumber(paymentData?.AccountNumber)) {
      return 'Invalid or missing account number';
    }

    // Validate payment amount
    if (!this.validatePaymentAmount(paymentData?.Amount)) {
      return 'Invalid payment amount. Amount must be greater than 0';
    }

    // Validate payment token for saved payment methods
    if (paymentData?.Token && typeof paymentData.Token !== 'string') {
      return 'Invalid payment token';
    }

    return null;
  }

  /**
   * Validates payment amount
   * @param amount Payment amount to validate
   * @returns true if valid, false otherwise
   */
  private validatePaymentAmount(amount: number | undefined): boolean {
    if (amount === undefined || amount === null) {
      return false;
    }

    // Must be a number
    if (typeof amount !== 'number' || isNaN(amount)) {
      return false;
    }

    // Must be positive
    if (amount <= 0) {
      return false;
    }

    // Check for reasonable maximum (e.g., $100,000)
    if (amount > 100000) {
      return false;
    }

    // Check for too many decimal places (max 2 for currency)
    const decimalPlaces = (amount.toString().split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      return false;
    }

    return true;
  }

  /**
   * Helper method to clone a PaymentMethod object
   * Uses spread operator for shallow clone - appropriate for PaymentMethod which has no nested objects
   * This avoids JSON.parse(JSON.stringify()) which loses dates, functions, and has poor performance
   * @param paymentMethod Payment method to clone
   * @returns Cloned payment method object
   */
  private clonePaymentMethod(paymentMethod: PaymentMethod): PaymentMethod {
    return { ...paymentMethod };
  }

  /**
   * Helper method to clone an array of PaymentMethod objects
   * @param paymentMethods Array of payment methods to clone
   * @returns New array with cloned payment method objects
   */
  private clonePaymentMethods(paymentMethods: PaymentMethod[]): PaymentMethod[] {
    return paymentMethods.map((pm) => this.clonePaymentMethod(pm));
  }
}
